import { useState, useEffect } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api from "../../../utils/axiosInstance";
import BackButton from "../../../components/Button/BackButton";

const fmtNum = (v) =>
  v !== undefined && v !== null && v !== "" ? Number(v).toFixed(2) : "—";

const fmtDate = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleString("en-IN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const Field = ({ label, value, children }) => (
  <div className="flex items-start">
    <span className="w-44 text-sm font-medium text-gray-700 shrink-0">
      {label}:
    </span>
    <span className="text-sm text-gray-900">
      {children ?? value ?? "—"}
    </span>
  </div>
);

const VendorReceivableDetailPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type") || "flat_rate";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/vendor-receivables/${id}?type=${type}`, {
          withCredentials: true,
        });
        setData(res.data.data);
      } catch (err) {
        console.error(err);
        setError("Failed to load receivable details");
      } finally {
        setLoading(false);
      }
    };
    if (id) fetch();
  }, [id, type]);

  if (loading)
    return (
      <div className="flex justify-center py-10">
        <div className="animate-spin rounded-full h-8 w-8 border border-gray-300 border-t-blue-600" />
      </div>
    );
  if (error) return <p className="text-red-600 text-sm">{error}</p>;
  if (!data) return <p className="text-sm text-gray-500">Receivable not found.</p>;

  const langPair = data.job?.languagePair
    ? `${data.job.languagePair.sourceLanguage?.name || ""} → ${
        data.job.languagePair.targetLanguage?.name || ""
      }`
    : "—";

  const currCode = data.currency?.currency?.code || "—";
  const typeName = data.type === "flat_rate" ? "Flat rate" : "Unit based";
  const code = `WL${data.id}`;

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <BackButton />
          <h1 className="text-2xl font-bold text-gray-900">
            Receivable {code}
          </h1>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg p-8 space-y-4 text-sm">
        <Field label="Invoice" value="—" />
        <Field label="Type" value={typeName} />
        <Field label="Job">
          <Link
            to={`/vendor/job/${data.job_id}`}
            className="text-blue-600 hover:text-blue-800 underline font-medium"
          >
            LT{data.job_id}
          </Link>
        </Field>
        <Field label="Job service" value={data.job?.service?.name || "—"} />
        <Field label="Job language pair" value={langPair} />

        {data.type === "unit_based" && (
          <>
            <p className="text-left font-bold text-base text-gray-900 pt-2 pb-2">
              Unit details
            </p>
            <Field label="Unit amount" value={fmtNum(data.unit_amount)} />
            <Field label="Unit" value={data.unit?.name || "—"} />
            <Field label="Price per unit" value={fmtNum(data.price_per_unit)} />
          </>
        )}

        <p className="text-left font-bold text-base text-gray-900 pt-2 pb-2">
          Subtotal &amp; total
        </p>
        <Field label="Subtotal" value={fmtNum(data.subtotal)} />
        <Field label="Extra charge" value="—" />
        <Field label="Discount" value="—" />
        <Field label="Total" value={fmtNum(data.subtotal)} />
        <Field label="Currency" value={currCode} />

        <p className="text-left font-bold text-base text-gray-900 pt-2 pb-2">
          Other details
        </p>
        <Field label="Filename" value={data.file?.file_name || "—"} />
        <Field label="Note for vendor" value={data.note_for_vendor || "—"} />
        <Field label="Issued at" value={fmtDate(data.createdAt)} />
      </div>
    </div>
  );
};

export default VendorReceivableDetailPage;
